import { Unit } from './types';

export const BASIC: Unit = {
  id: 'basic',
  number: 2,
  title: 'Basic Korean',
  description: 'Greetings, people, everyday words and your first sentences.',
  emoji: '👋',
  lessons: [
    {
      id: 'greetings',
      title: 'Greetings',
      summary: 'Hello, goodbye, sorry and it is okay',
      exercises: [
        { kind: 'teach', ko: '안녕하세요', roman: 'an-nyeong-ha-se-yo', en: 'hello', note: 'The polite hello. Use it with anyone, at any time of day.' },
        { kind: 'teach', ko: '안녕히 가세요', roman: 'an-nyeong-hi ga-se-yo', en: 'goodbye (to someone who is leaving)' },
        { kind: 'teach', ko: '죄송합니다', roman: 'joe-song-ham-ni-da', en: "I'm sorry" },
        { kind: 'teach', ko: '괜찮아요', roman: 'gwaen-cha-na-yo', en: "It's okay / I'm fine" },
        { kind: 'teach', ko: '반갑습니다', roman: 'ban-gap-seum-ni-da', en: 'Nice to meet you' },
        { kind: 'choice', prompt: 'You meet your manager in the morning. What do you say?', options: ['안녕하세요', '죄송합니다', '안녕히 가세요', '괜찮아요'], answer: 0 },
        { kind: 'choice', prompt: 'You are late for work. What do you say?', options: ['반갑습니다', '죄송합니다', '안녕하세요', '감사합니다'], answer: 1 },
        { kind: 'choice', prompt: 'Listen. What does it mean?', speak: '괜찮아요', listenOnly: true, options: ["It's okay", 'Hello', 'Goodbye', 'Thank you'], answer: 0 },
        { kind: 'match', prompt: 'Match the greeting to its meaning', pairs: [['안녕하세요', 'hello'], ['죄송합니다', 'sorry'], ['반갑습니다', 'nice to meet you'], ['괜찮아요', "it's okay"]] },
      ],
    },
    {
      id: 'people',
      title: 'People',
      summary: 'I, friend, boss, coworker and family',
      exercises: [
        { kind: 'teach', ko: '저', roman: 'jeo', en: 'I, me (polite)' },
        { kind: 'teach', ko: '사람', roman: 'sa-ram', en: 'person', picture: '🧍' },
        { kind: 'teach', ko: '친구', roman: 'chin-gu', en: 'friend', picture: '🤝' },
        { kind: 'teach', ko: '사장님', roman: 'sa-jang-nim', en: 'the boss, the company owner', note: '님 (nim) is added to show respect.' },
        { kind: 'teach', ko: '동료', roman: 'dong-nyo', en: 'coworker' },
        { kind: 'teach', ko: '가족', roman: 'ga-jok', en: 'family', picture: '👨‍👩‍👧' },
        { kind: 'choice', prompt: 'What does this mean?', ko: '친구', options: ['friend', 'family', 'boss', 'person'], answer: 0 },
        { kind: 'choice', prompt: 'Who owns the company?', options: ['사장님', '동료', '친구', '아이'], answer: 0 },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['저', 'I'], ['사람', 'person'], ['동료', 'coworker'], ['가족', 'family']] },
      ],
    },
    {
      id: 'common-words',
      title: 'Common words',
      summary: 'This, here, now, have and good',
      exercises: [
        { kind: 'teach', ko: '이거', roman: 'i-geo', en: 'this (thing)' },
        { kind: 'teach', ko: '여기', roman: 'yeo-gi', en: 'here' },
        { kind: 'teach', ko: '지금', roman: 'ji-geum', en: 'now' },
        { kind: 'teach', ko: '있어요', roman: 'i-sseo-yo', en: 'there is / I have' },
        { kind: 'teach', ko: '없어요', roman: 'eop-seo-yo', en: "there isn't / I don't have" },
        { kind: 'teach', ko: '좋아요', roman: 'jo-a-yo', en: "good / I like it" },
        { kind: 'choice', prompt: 'What does this mean?', ko: '지금', options: ['now', 'here', 'this', 'good'], answer: 0 },
        { kind: 'choice', prompt: 'Which word is the opposite of 있어요?', options: ['없어요', '좋아요', '여기', '이거'], answer: 0 },
        { kind: 'choice', prompt: 'Listen. What does it mean?', speak: '여기', listenOnly: true, options: ['here', 'now', 'this', 'good'], answer: 0 },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['이거', 'this'], ['여기', 'here'], ['좋아요', 'good'], ['없어요', "there isn't"]] },
      ],
    },
    {
      id: 'verbs',
      title: 'Verbs',
      summary: 'Go, come, eat, drink, work and sleep',
      exercises: [
        { kind: 'teach', ko: '가요', roman: 'ga-yo', en: 'go', picture: '🚶', note: 'Verbs ending in -요 (yo) are the polite everyday form.' },
        { kind: 'teach', ko: '와요', roman: 'wa-yo', en: 'come' },
        { kind: 'teach', ko: '먹어요', roman: 'meo-geo-yo', en: 'eat', picture: '🍽️' },
        { kind: 'teach', ko: '마셔요', roman: 'ma-syeo-yo', en: 'drink', picture: '🥤' },
        { kind: 'teach', ko: '일해요', roman: 'il-hae-yo', en: 'work', picture: '👷' },
        { kind: 'teach', ko: '자요', roman: 'ja-yo', en: 'sleep', picture: '😴' },
        { kind: 'choice', prompt: 'Listen. Which picture matches?', speak: '자요', listenOnly: true, options: ['😴', '🍽️', '👷', '🚶'], pictureOptions: true, answer: 0, explain: '자요 (ja-yo) means "sleep".' },
        { kind: 'choice', prompt: 'Which verb means "drink"?', options: ['마셔요', '먹어요', '가요', '와요'], answer: 0 },
        { kind: 'match', prompt: 'Match the verb to its meaning', pairs: [['가요', 'go'], ['와요', 'come'], ['먹어요', 'eat'], ['일해요', 'work']] },
      ],
    },
    {
      id: 'simple-sentences',
      title: 'Simple sentences',
      summary: 'Thing + verb, and "I am …"',
      exercises: [
        { kind: 'teach', ko: '물을 마셔요', roman: 'mu-reul ma-syeo-yo', en: 'I drink water', note: 'The thing comes first and the verb comes last. 을 marks the thing after a consonant; 를 is used after a vowel.' },
        { kind: 'teach', ko: '우유를 마셔요', roman: 'u-yu-reul ma-syeo-yo', en: 'I drink milk', note: '우유 ends in a vowel, so it takes 를.' },
        { kind: 'teach', ko: '회사에 가요', roman: 'hoe-sa-e ga-yo', en: 'I go to the company', note: '에 (e) means "to" a place.' },
        { kind: 'teach', ko: '저는 네팔 사람이에요', roman: 'jeo-neun ne-pal sa-ra-mi-e-yo', en: 'I am Nepali', note: '는 marks the topic: "as for me". Put your own country in place of 네팔.' },
        { kind: 'choice', prompt: 'Choose the word for the blank', ko: '밥을 ___', options: ['먹어요', '마셔요', '자요', '와요'], answer: 0, explain: '밥을 먹어요: I eat a meal.' },
        { kind: 'choice', prompt: 'Choose the word for the blank', ko: '물을 ___', options: ['자요', '마셔요', '가요', '일해요'], answer: 1, explain: '물을 마셔요: I drink water.' },
        { kind: 'choice', prompt: 'What does this mean?', ko: '회사에 가요', options: ['I go to the company', 'I eat at the company', 'I sleep at home', 'I drink water'], answer: 0 },
        { kind: 'choice', prompt: 'Listen. What did you hear?', speak: '집에 가요', listenOnly: true, options: ['I go home', 'I drink milk', 'I work', 'I eat rice'], answer: 0, explain: '집에 가요: I go home.' },
      ],
    },
  ],
};
